import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, AlertCircle } from "lucide-react";
import { SpnLogo } from "@/components/brand/spn-logo";
import {
  useConfirmUeiVerification,
  getGetMeQueryKey,
  getGetMyVerificationQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function VerifyUeiPage() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const ranRef = useRef(false);

  const confirmMut = useConfirmUeiVerification({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        qc.invalidateQueries({ queryKey: getGetMyVerificationQueryKey() });
        toast.success("Workspace UEI verified");
        setDone(true);
      },
      onError: (e) => {
        const msg = e?.message ?? "This verification link is invalid or has expired.";
        setError(msg);
        toast.error(msg);
      },
    },
  });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError("Missing token in verification link.");
      return;
    }
    confirmMut.mutate({ data: { token } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-emerald-50 p-6">
      <Card className="relative w-full max-w-md shadow-xl">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            {error ? <AlertCircle className="h-5 w-5" /> : <BadgeCheck className="h-5 w-5" />}
          </div>
          <CardTitle className="text-2xl">
            {error
              ? "Verification link problem"
              : done
                ? "Workspace verified"
                : "Verifying your UEI…"}
          </CardTitle>
          <CardDescription>
            {error
              ? "Ask the workspace owner to send a fresh verification email."
              : done
                ? "Your workspace will now show a Verified badge across the network."
                : "Confirming control of your UEI via the SAM.gov point of contact."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!error && !done && (
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4 text-emerald-700" />
              <span>Hang tight — this only takes a moment.</span>
            </div>
          )}
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          {(done || error) && (
            <Button
              className="w-full gap-2"
              onClick={() => navigate(done ? "/vendor-profile" : "/")}
            >
              {done ? "Open vendor profile" : "Back to sign in"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
            <SpnLogo className="h-3.5 w-3.5" />
            Synergistic Partner Network
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
